import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { submitToWeb3Forms } from "@/lib/web3forms";
import { PageHeader } from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CATEGORIES = [
  { id: "bug", label: "Bug report" },
  { id: "feature", label: "Feature request" },
  { id: "question", label: "Question" },
  { id: "other", label: "Other" },
];

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#16324F] focus:border-[#16324F] outline-none";
const EMPTY = { category: "bug", message: "", email: "" };

export default function Feedback() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) {
      toast.error("Tell us something first: the message field is empty.");
      return;
    }
    setBusy(true);
    const categoryLabel = CATEGORIES.find((c) => c.id === form.category)?.label || form.category;
    const email = form.email.trim();
    try {
      // Two destinations, deliberately: the backlog table keeps the record even
      // if mail delivery fails, and Web3Forms puts it in the shared inbox.
      // Settled rather than awaited in sequence, because one of the two failing
      // is not the same thing as the feedback being lost. Awaiting the API call
      // first reported "Not Found" over a mail that had already been delivered,
      // and the Web3Forms result was never inspected at all, so a failed mail
      // with a stored row looked identical to a clean send.
      const [stored, mailed] = await Promise.allSettled([
        api.post("/feedback", {
          category: form.category,
          message: form.message.trim(),
          email: email || null,
        }),
        submitToWeb3Forms(
          {
            category: categoryLabel,
            message: form.message.trim(),
            email: email || "not provided",
          },
          {
            subject: `Complaudax feedback: ${categoryLabel}`,
            from_name: "Complaudax dashboard",
            replyto: email,
          }
        ),
      ]);

      // submitToWeb3Forms reports failure in its return value, not by throwing.
      const recorded = stored.status === "fulfilled";
      const delivered = mailed.status === "fulfilled" && mailed.value.ok;

      if (!recorded && !delivered) {
        // The reason goes to the console rather than the toast: a raw upstream
        // detail like "Not Found" tells the person who wrote in nothing.
        console.error("Feedback failed to send", stored.reason, mailed.value ?? mailed.reason);
        toast.error("Could not send feedback. Try again.");
        return;
      }
      if (!recorded || !delivered) {
        console.warn("Feedback reached only one destination", { recorded, delivered });
      }
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="feedback-page">
      <PageHeader
        title="Feedback"
        description="Bugs, ideas, questions: everything lands directly in our backlog and gets read."
      />

      <form onSubmit={submit} className="bg-white border border-gray-200 rounded-sm p-5 max-w-2xl" data-testid="feedback-form">
        <label htmlFor="feedback-category" className="block text-sm font-medium mb-1.5">Category</label>
        <select
          id="feedback-category"
          data-testid="feedback-category-select"
          value={form.category}
          onChange={set("category")}
          className={`${inputCls} mb-4 bg-white`}
        >
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>

        <label htmlFor="feedback-message" className="block text-sm font-medium mb-1.5">Message</label>
        <textarea
          id="feedback-message"
          data-testid="feedback-message-input"
          value={form.message}
          onChange={set("message")}
          rows={6}
          required
          maxLength={5000}
          placeholder="What happened, or what should exist?"
          className={`${inputCls} mb-4`}
        />

        <label htmlFor="feedback-email" className="block text-sm font-medium mb-1.5">
          Email for a reply <span className="font-normal text-ink-muted">(optional)</span>
        </label>
        <input
          id="feedback-email"
          type="email"
          data-testid="feedback-email-input"
          value={form.email}
          onChange={set("email")}
          placeholder="you@example.com"
          autoComplete="email"
          className={`${inputCls} mb-1.5 font-mono`}
        />
        <p className="text-xs text-ink-muted mb-4">Only used to reply to this message. Leave it empty to stay anonymous.</p>

        <button
          type="submit"
          data-testid="feedback-submit-btn"
          disabled={busy}
          className="bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
        >
          {busy ? "Sending..." : "Send feedback"}
        </button>
      </form>

      <Dialog open={sent} onOpenChange={(open) => { if (!open) { setSent(false); setForm(EMPTY); } }}>
        <DialogContent data-testid="feedback-confirm-modal">
          <DialogHeader>
            <DialogTitle>Feedback sent</DialogTitle>
            <DialogDescription>
              Thank you. Your feedback was recorded and helps decide what gets built next.
              {form.email.trim() ? " We will reply to the email you provided if a follow up is needed." : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              data-testid="feedback-confirm-close-btn"
              onClick={() => { setSent(false); setForm(EMPTY); }}
              className="bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
